import {
  OutlinedInput,
  FormControl,
  InputLabel,
  Autocomplete,
  FormControlLabel,
  Switch,
  Stack,
  Chip,
  Box,
  Alert,
  Divider,
  Grid,
  Button,
  Typography,
} from "@mui/material";
import { useCallback, Dispatch, forwardRef, SetStateAction, useEffect, useState, useMemo } from "react";

import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import FormSection from "../../../Forms/FormSection/form-section";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  ShareFormValues,
  ShareLocalizationSection,
  ShareRecipientsSection,
  ShareSecuritySection,
  ShareSettingsSection,
} from "../modal-create-share";
import { userService, cacheService } from "../../../../Core/Api/services";
import moment from "moment";
import { WatermarkTemplates } from "../../../../Core/Api/Types/watermark-templates";
import { MailingListDto } from "../../../../Core/Api/Types/get-mailing-list-response";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { produce } from "immer";
import { DbUserBasicInfos } from "../../../../Core/Api/Types/db-user-basic-infos";
import { ShareMediaType } from "../../../../Core/Api/Types/share-media-type";
import {
  UserHasAcl,
  GetShareMediaType,
  IsListOfEmails,
  IsEmail,
  timeSpanToMinutes,
  minutesToTimeSpan,
} from "../../../../Utils/helpers";
import FormInputEmail from "../../../Forms/FormInputEmail/form-input-email";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import { DbAssetObjectInfos } from "../../../../Core/Api/Types/db-asset-object-infos";
import { TitleShouldBeWatermarked } from "../../../../Core/Utils/asset-object-utils";
import SecurityIcon from "@mui/icons-material/Security";
import { RecentEmailContext } from "../../../../Core/Api/Types/get-recent-emails-response";
import { useTranslation } from "react-i18next";

export type ModalCreateShareStepThreeElementProps = {
  objectLayer?: ObjectLayer | null;
  securitySection: ShareSecuritySection;
  settingsSection: ShareSettingsSection;
  localizationSection: ShareLocalizationSection;
  recipientsSection: ShareRecipientsSection;
  isFormValid: boolean;
  securityText: string[];
  isMultiSelect: boolean;
  isNameMandatory?: boolean;
  title?: DbAssetObjectInfos;
  setSecurityText: Dispatch<SetStateAction<string[]>>;
  setSecuritySection: Dispatch<SetStateAction<ShareSecuritySection>>;
  setSettingsSection: Dispatch<SetStateAction<ShareSettingsSection>>;
  setLocalizationSection: Dispatch<SetStateAction<ShareLocalizationSection>>;
  setRecipientsSection: Dispatch<SetStateAction<ShareRecipientsSection>>;
  setFormValues: Dispatch<SetStateAction<ShareFormValues>>;
};

const ModalCreateShareStepThree = forwardRef<any, ModalCreateShareStepThreeElementProps>((props, ref) => {
  const { t } = useTranslation();
  const {
    objectLayer,
    securitySection,
    settingsSection,
    localizationSection,
    recipientsSection,
    isFormValid,
    securityText,
    isMultiSelect,
    isNameMandatory = false,
    title,
    setSecuritySection,
    setSettingsSection,
    setLocalizationSection,
    setRecipientsSection,
    setFormValues,
    setSecurityText,
  } = props;
  const [securityValidation, setSecurityValidation] = useState<boolean>(true);
  const [searchEmailValue, setSearchEmailValue] = useState<DbUserBasicInfos | null>(null);
  const [localizationValidation, setLocalizationValidation] = useState<boolean>(
    localizationSection.localizationAudioSelected.length > 0 ||
      objectLayer === ObjectLayer.Asset ||
      ((objectLayer === ObjectLayer.Title ||
        objectLayer === ObjectLayer.Group ||
        objectLayer === ObjectLayer.Head ||
        objectLayer === undefined) &&
        isMultiSelect)
  );
  const [emails, setEmails] = useState<DbUserBasicInfos[]>([]);
  const [expandSection1, setExpandSection1] = useState<boolean>(
    localizationSection.audioLanguageOptions.length === 0 ? true : localizationValidation ? false : true
  );
  const [expandSection2, setExpandSection2] = useState<boolean>(false);
  const [expandSection3, setExpandSection3] = useState<boolean>(false);
  const [expandSection4, setExpandSection4] = useState<boolean>(recipientsSection.emails.length > 0 ? false : true);
  const [expandOrCollapse, setExpandOrCollapse] = useState<boolean>(true);
  const [onTheFlyEmailOverlay, setOnTheFlyEmailOverlay] = useState<boolean>(false);
  const [inputEmail, setInputEmail] = useState<string>("");
  const [recentEmails, setRecentEmails] = useState<string[]>([]);

  useEffect(() => {
    if (!securityValidation) setExpandSection3(!securityValidation);
  }, [securityValidation]);

  useEffect(() => {
    userService.GetMailingList().then(function (response) {
      if (response !== null) {
        let list: MailingListDto[] = response;
        list.filter(
          (element) => element.emails.length !== 0 || element.emails !== null || element.emails !== undefined
        );
        setRecipientsSection((prevState) => ({
          ...prevState,
          emailList: list,
        }));
      }
    });

    setFormValues((prevState) => ({
      ...prevState,
      isFormValid:
        securityValidation &&
        localizationValidation &&
        recipientsSection.emails.length > 0 &&
        settingsSection.expirationDate !== null &&
        (settingsSection.maxViews !== null || settingsSection.maxViews !== 0),
    }));
  }, []);

  useEffect(() => {
    let validation: boolean =
      securityValidation &&
      localizationValidation &&
      recipientsSection.emails.length > 0 &&
      settingsSection.expirationDate !== null &&
      (settingsSection.maxViews !== null || settingsSection.maxViews !== 0);
    if (validation !== isFormValid)
      setFormValues((prevState) => ({
        ...prevState,
        isFormValid: validation,
      }));
  }, [recipientsSection, securitySection, settingsSection, localizationSection]);

  useEffect(() => {
    let isSecurityPartValid: boolean = false;
    if (
      (onTheFlyEmailOverlay ||
        securitySection.securityMode === ShareMediaType.StreamingBasic ||
        (securitySection.watermarkContent &&
          securitySection.watermarkContent?.length >= GlobalConst.SHARE_OVERLAYTEXT_MIN_CHARS)) &&
      securitySection.securityMode
    )
      isSecurityPartValid = true;
    setSecurityValidation(isSecurityPartValid);

    let securityStatusText: string[] = [];
    if (securitySection.maxIps)
      securityStatusText.push(t("form:statusText.maxIPs", { maxIps: securitySection.maxIps.toString() }));
    if (securitySection.maxClips)
      securityStatusText.push(t("form:statusText.maxClips", { maxClips: securitySection.maxClips.toString() }));
    if (securitySection.maxClipDuration)
      securityStatusText.push(
        t("form:statusText.maxClipDuration", { maxClipDuration: securitySection.maxClipDuration })
      );

    if (securitySection.securityMode)
      securityStatusText.push(
        t("form:statusText.securityMode", { securityMode: securitySection.securityMode.toString() })
      );

    setSecurityText(securityStatusText);
  }, [securitySection, onTheFlyEmailOverlay]);

  const handleKeyDown = (event) => {
    if (event.key !== "Enter") return;
    if (event.target.value.length === 0) return;

    let listDelimiter = IsListOfEmails(event.target.value);

    if (listDelimiter !== null) {
      let emails: string[] = event.target.value.split(listDelimiter);
      emails = emails.map((x) => x.trim());

      for (let i = 0; i < emails.length; i++) {
        if (!IsEmail(emails[i])) return;

        if (!recipientsSection.emails.includes(emails[i]))
          setRecipientsSection((prevState) => ({
            ...prevState,
            emails: [...prevState.emails, emails[i]],
          }));
      }
    } else {
      if (!IsEmail(event.target.value)) return;
      else {
        if (!recipientsSection.emails.includes(event.target.value))
          setRecipientsSection((prevState) => ({
            ...prevState,
            emails: [...prevState.emails, event.target.value],
          }));
      }
    }

    setInputEmail("");
  };

  const onDeleteEmail = (email) => {
    setRecipientsSection((prevState) => ({
      ...prevState,
      emails: prevState.emails.filter((x) => x !== email),
    }));
  };

  const addEmailFromlist = (value: string[]) => {
    if (value === null) return;
    let emails: string[] = recipientsSection.emails;
    value.forEach((email: string) => {
      if (!recipientsSection.emails.includes(email)) {
        emails.push(email);
      }
    });

    setRecipientsSection((prevState) => ({
      ...prevState,
      emails: emails,
    }));
  };

  const onChangeSecurity = (key: keyof ShareSecuritySection, value: any) => {
    setSecuritySection((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const onChangeSearchEmail = (entry: string) => {
    setSearchEmailValue(null);
    if (entry === "" || entry === undefined || entry === null) {
      setEmails([]);
      return;
    }
    userService.SearchUsersBasic(entry).then(function (response) {
      if (response.success && response.users) setEmails(response.users);
    });
  };

  const onChangeLocalization = useCallback(
    (key: number, value: any) => {
      if (value === "") value = null;
      if (key === 1) {
        // Map/Patch audio version
        for (let i = 0; i < value.length; ++i) {
          if (value[i].audioVersion.includes("Original")) value[i].audioVersion = "OV";
        }

        setLocalizationSection((prevState) => ({
          ...prevState,
          localizationAudioSelected: value,
        }));
        setLocalizationValidation(localizationSection.audioLanguageOptions.length > 0 && value.length > 0);
      } else {
        setLocalizationSection((prevState) => ({
          ...prevState,
          localizationSubtitlesSelected: value,
        }));
      }
    },
    [setLocalizationSection]
  );

  const onChangeSettings = useCallback(
    (key: keyof ShareSettingsSection, value: any) => {
      if (value === "") value = null;
      setSettingsSection((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [setSettingsSection]
  );

  const onClickExpandAll = () => {
    let tmp: boolean = true;
    if (!expandOrCollapse) tmp = false;
    setExpandSection1(tmp);
    setExpandSection2(tmp);
    setExpandSection3(tmp);
    setExpandSection4(tmp);
    setExpandOrCollapse(!expandOrCollapse);
  };

  const securityModes = UserHasAcl(AclTypes.aclFastModeShare)
    ? [ShareMediaType.StreamingBasic, ShareMediaType.StreamingTagSecure]
    : [ShareMediaType.StreamingTagSecure];

  const maxViewDays: number = useMemo(() => {
    let res = cacheService.GetGroup()!.maxShareViewDays
      ? cacheService.GetGroup()!.maxShareViewDays
      : cacheService.GetPlatformSettings()?.maxShareViewDays ?? 1000;

    if (res === null) res = 1000;

    setExpandSection2(
      settingsSection.expirationDate &&
        settingsSection.expirationDate <= res &&
        settingsSection.maxViews &&
        (settingsSection.shareName || !isNameMandatory)
        ? false
        : true
    );

    return res;
  }, [cacheService]);

  const audioVersionOrder = useMemo(() => ["OV", "Dubbed", "Commentary", "MusicAndEffects"], []);

  useEffect(() => {
    userService.GetRecentEmails(RecentEmailContext.Share).then((response) => {
      if (response.emails) setRecentEmails(response.emails);
    });
  }, []);

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent={"flex-end"}>
        <Button onClick={onClickExpandAll} size="small">
          {expandOrCollapse ? t("button:expandAll") : t("button:collapseAll")}{" "}
          {expandOrCollapse && <AddIcon sx={{ ml: 1 }} />} {!expandOrCollapse && <RemoveIcon sx={{ ml: 1 }} />}{" "}
        </Button>
      </Stack>
      {objectLayer !== ObjectLayer.Asset &&
        !(
          (objectLayer === ObjectLayer.Title ||
            objectLayer === ObjectLayer.Group ||
            objectLayer === ObjectLayer.Head ||
            objectLayer === undefined) &&
          isMultiSelect
        ) && (
          <FormSection
            icon={<LanguageOutlinedIcon />}
            expanded={expandSection1}
            onExpand={() => setExpandSection1(!expandSection1)}
            title={t("common:localization")}
            status={
              localizationSection.audioLanguageOptions.length === 0
                ? "error"
                : localizationValidation
                ? "complete"
                : "todo"
            }
            statusText={
              localizationSection.audioLanguageOptions.length === 0
                ? t("form:helper.noAudioLanguageAvailable")
                : localizationValidation
                ? [
                    localizationSection.localizationAudioSelected.map((element) => {
                      return element.audioType + "-" + element.value;
                    }),
                    localizationSection.localizationSubtitlesSelected.map((element) => {
                      return element.subtitleType + "-" + element.value;
                    }),
                  ].join(" · ")
                : t("common:configurationRequired")
            }
          >
            <FormControl sx={{ mb: 1 }} required>
              <InputLabel htmlFor="localizationAudio" sx={{ mb: 1 }}>
                {t("field:label.audioLanguage")}
              </InputLabel>
              <Autocomplete
                id="localizationAudio"
                multiple
                options={localizationSection.audioLanguageOptions.sort((a, b) => {
                  const audioVersionA = a.audioVersion;
                  const audioVersionB = b.audioVersion;

                  if (audioVersionA === audioVersionB) return 0;

                  if (audioVersionA === null || audioVersionB === null)
                    // If one of them is null, prioritize the non-null one
                    return audioVersionA === null ? 1 : -1;

                  const indexA = audioVersionOrder.indexOf(audioVersionA);
                  const indexB = audioVersionOrder.indexOf(audioVersionB);

                  // If both values are in the predefined order, compare them
                  if (indexA > -1 && indexB > -1) {
                    return indexA - indexB;
                  }

                  // If only one of them is in the predefined order, prioritize that one
                  if (indexA > -1) {
                    return -1;
                  }

                  if (indexB > -1) {
                    return 1;
                  }

                  // If none of them are in the predefined order, sort alphabetically
                  return audioVersionA.localeCompare(audioVersionB);
                })}
                groupBy={(option) => option.audioVersion!}
                onChange={(e, value) => onChangeLocalization(1, value)}
                popupIcon={<ExpandMoreRoundedIcon />}
                getOptionLabel={(option) => option.label!}
                value={localizationSection.localizationAudioSelected}
                isOptionEqualToValue={(option) =>
                  localizationSection.localizationAudioSelected.some(
                    (x) => x.audioType === option.audioType && x.value === option.value
                  )
                }
                renderOption={(props, option) => (
                  <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
                    {option.label}
                  </Box>
                )}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    placeholder={t("field:placeholder.selectAudio")}
                    required
                    sx={{ width: "100%" }}
                  />
                )}
              />
            </FormControl>
            <FormControl>
              <InputLabel htmlFor="localizationSubtitles" sx={{ mb: 1 }}>
                {t("field:label.subtitleLanguage")}
              </InputLabel>
              <Autocomplete
                id="localizationSubtitles"
                options={localizationSection.subtitleLanguageOptions.sort(
                  (a, b) => b.subtitleType?.localeCompare(a.subtitleType || "") || -1
                )}
                onChange={(e, value) => onChangeLocalization(2, value)}
                multiple
                groupBy={(option) => option.subtitleType!}
                getOptionLabel={(option) => option.label!}
                value={localizationSection.localizationSubtitlesSelected}
                isOptionEqualToValue={(option) =>
                  localizationSection.localizationSubtitlesSelected.some(
                    (element) => element.subtitleType === option.subtitleType && element.value === option.value
                  )
                }
                popupIcon={<ExpandMoreRoundedIcon />}
                renderOption={(props, option, index) => {
                  const key = `listItem-${index}-${option.value}`;
                  return (
                    <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props} key={key}>
                      {option.label}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    placeholder={t("field:placeholder.selectSubtitles")}
                    required
                    sx={{ width: "100%" }}
                  />
                )}
              />
            </FormControl>
          </FormSection>
        )}
      <FormSection
        icon={<SettingsOutlinedIcon />}
        expanded={expandSection2}
        onExpand={() => setExpandSection2(!expandSection2)}
        title={t("form:section.title.shareSettings")}
        status={
          settingsSection.expirationDate &&
          settingsSection.expirationDate <= maxViewDays &&
          settingsSection.maxViews &&
          (settingsSection.shareName || !isNameMandatory)
            ? "complete"
            : "warning"
        }
        statusText={
          settingsSection.expirationDate &&
          settingsSection.expirationDate <= maxViewDays &&
          settingsSection.maxViews &&
          (settingsSection.shareName || !isNameMandatory)
            ? [
                settingsSection.shareName ||
                  (!isNameMandatory ? t("form:helper.autoShareName") : t("form:helper.noName")),
                moment().add(settingsSection.expirationDate, "days").format("L"),
                settingsSection.maxViews + " max views",
                settingsSection.comment || t("form:helper.noComment"),
              ].join(" · ")
            : t("common:configurationRequired")
        }
      >
        <FormControl sx={{ mb: 1 }} required={isNameMandatory}>
          <InputLabel shrink htmlFor="localized-title">
            {t("field:label.shareName")}
          </InputLabel>
          <OutlinedInput
            id="shareName"
            value={settingsSection.shareName || ""}
            onChange={(e) => onChangeSettings("shareName", e.currentTarget.value)}
            placeholder={t("field:placeholder.shareName")}
          />
        </FormControl>
        <FormControl sx={{ mb: 1 }} required>
          <InputLabel shrink htmlFor="expirationDate" sx={{ mb: 1 }}>
            {(settingsSection.expirationDate
              ? t("field:label.expiresOn") +
                " " +
                moment().add(settingsSection.expirationDate, "days").format("Do MMM YYYY")
              : t("field:label.expiresOn")) +
              " (" +
              t("common:max") +
              " " +
              (cacheService.GetGroup()!.maxShareViewDays
                ? cacheService.GetGroup()!.maxShareViewDays
                : cacheService.GetPlatformSettings()?.maxShareViewDays) +
              ")"}
          </InputLabel>
          <OutlinedInput
            id="expirationDate"
            type="text"
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              max: maxViewDays,
            }}
            value={settingsSection.expirationDate ?? ""}
            onChange={(e) => {
              let inputValue = e.currentTarget.value;
              let numericValue = inputValue === "" ? null : parseInt(inputValue.replace(/\D/g, "")); // Handle empty input

              if (inputValue === "" || !maxViewDays || (numericValue !== null && numericValue <= maxViewDays)) {
                onChangeSettings("expirationDate", numericValue);
              }
            }}
            placeholder={t("field:placeholder.validityInDays")}
          />
        </FormControl>
        <FormControl sx={{ mb: 1 }} required>
          <InputLabel shrink htmlFor="maxViews">
            {t("field:label.maxViewsWithLimit", {
              views: cacheService.GetGroup()!.maxShareViews
                ? cacheService.GetGroup()!.maxShareViews
                : cacheService.GetPlatformSettings()?.maxShareViews,
            })}
          </InputLabel>
          <OutlinedInput
            id="maxViews"
            type="text"
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              max: cacheService.GetGroup()!.maxShareViews
                ? cacheService.GetGroup()!.maxShareViews
                : cacheService.GetPlatformSettings()?.maxShareViews ?? 1000,
            }}
            value={settingsSection.maxViews ?? ""}
            onChange={(e) => {
              let inputValue = e.currentTarget.value;
              let numericValue = inputValue === "" ? null : parseInt(inputValue.replace(/\D/g, "")); // Handle empty input

              let max = cacheService.GetGroup()!.maxShareViews
                ? cacheService.GetGroup()!.maxShareViews
                : cacheService.GetPlatformSettings()?.maxShareViews ?? 1000;

              if (inputValue === "" || !max || (numericValue !== null && numericValue <= max)) {
                onChangeSettings("maxViews", numericValue);
              }
            }}
            placeholder={
              cacheService.GetGroup()!.maxShareViews?.toString() ?? t("field:placeholder.enterMaxViewNumber")
            }
          />
        </FormControl>
        <FormControl>
          <InputLabel shrink htmlFor="comment">
            {t("common:comment")}
          </InputLabel>
          <OutlinedInput
            id="comment"
            multiline
            value={settingsSection.comment || ""}
            onChange={(e) => onChangeSettings("comment", e.currentTarget.value)}
            placeholder={t("field:placeholder.enjoyLatestContent")}
            name="watermark_comment"
            rows={10}
          />
        </FormControl>
      </FormSection>
      <FormSection
        icon={<LockOutlinedIcon />}
        expanded={expandSection3}
        onExpand={() => setExpandSection3(!expandSection3)}
        title={t("common:security")}
        status={securityValidation ? "complete" : "warning"}
        statusText={securityValidation ? securityText.join(" · ") : t("common:configurationRequired")}
      >
        {!cacheService.GetPlatformSettings()?.wowzaUseHlsChunker && (
          <FormControl sx={{ mb: 1 }} required>
            <InputLabel htmlFor="securityMode" sx={{ mb: 1 }}>
              {t("field:label.securityMode")}
            </InputLabel>
            <Autocomplete
              id="securityMode"
              options={securityModes}
              onChange={(e, value) =>
                setSecuritySection(
                  produce((draft) => {
                    draft!.securityMode = value;
                  })
                )
              }
              popupIcon={<ExpandMoreRoundedIcon />}
              getOptionLabel={(value: ShareMediaType) => GetShareMediaType(value) || ""}
              value={securitySection.securityMode}
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(params) => (
                <OutlinedInput
                  {...params.InputProps}
                  inputProps={params.inputProps}
                  placeholder={t("field:placeholder.securityMode")}
                  required
                  sx={{ width: "100%" }}
                />
              )}
            />
          </FormControl>
        )}

        <>
          <FormControl sx={{ mb: 1 }} required>
            <InputLabel htmlFor="watermarkTemplate" sx={{ mb: 1 }}>
              {t("field:label.watermarkTemplate")}
            </InputLabel>
            <Autocomplete
              id="watermarkTemplate"
              options={Object.values(WatermarkTemplates)}
              onChange={(e, value) => onChangeSecurity("watermarkTemplate", value ? WatermarkTemplates[value] : null)}
              popupIcon={<ExpandMoreRoundedIcon />}
              getOptionLabel={(value: string) => value || ""}
              value={securitySection.watermarkTemplate}
              renderInput={(params) => (
                <OutlinedInput
                  {...params.InputProps}
                  inputProps={params.inputProps}
                  placeholder={t("field:placeholder.positionOfTextOverlay")}
                  required
                  sx={{ width: "100%" }}
                />
              )}
            />
          </FormControl>
          <FormControl sx={{ mb: 1 }} required={securitySection.securityMode !== ShareMediaType.StreamingBasic}>
            <InputLabel shrink htmlFor="watermarkContent" sx={{ mb: 1 }}>
              {t("field:label.watermarkContent")}
            </InputLabel>
            <OutlinedInput
              id="watermarkContent"
              name="watermark_content"
              value={securitySection.watermarkContent || ""}
              placeholder={t("field:placeholder.enterRecipientNameOrCompany")}
              onChange={(e) => onChangeSecurity("watermarkContent", e.currentTarget.value)}
              disabled={onTheFlyEmailOverlay}
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              {t("alert:info.watermarkContentWillOnlyAppliedToVideos")}
            </Alert>
          </FormControl>
          {securitySection.watermarkTemplate &&
            cacheService.GetPlatformSettings()?.wowzaUseHlsChunker &&
            (!title || !TitleShouldBeWatermarked(title)) && (
              <FormControlLabel
                control={
                  <Switch
                    onChange={(e) => {
                      let chk = e.currentTarget.checked;
                      setOnTheFlyEmailOverlay(chk);
                      setFormValues(
                        produce((draft) => {
                          draft.onTheFlyEmailOverlay = chk;
                        })
                      );
                    }}
                  />
                }
                label={t("field:label.createLink")}
              />
            )}
          <FormControlLabel
            control={<Switch onChange={(e) => onChangeSecurity("printTimeCode", e.currentTarget.checked)} />}
            label={t("field:label.printTimecode")}
          />

          {title && TitleShouldBeWatermarked(title) && (
            <Alert sx={{ mt: 1 }} severity="info" icon={<SecurityIcon />}>
              {t("alert:info.thisShareWillBeWatermarkedWithNexguard")}
            </Alert>
          )}
        </>
        <Divider sx={{ mt: 2, mb: 2 }} />

        <Grid container spacing={3}>
          <Grid item md={4}>
            <FormControl sx={{ mb: 1 }}>
              <InputLabel shrink htmlFor="maxIps">
                {t("field:label.maxNumberOfIps")}
              </InputLabel>
              <OutlinedInput
                id="maxIps"
                type="text"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  max: cacheService.GetGroup()!.maxShareViewIPs
                    ? cacheService.GetGroup()!.maxShareViewIPs
                    : cacheService.GetPlatformSettings()?.maxShareViewIPs ?? 1000,
                }}
                value={securitySection.maxIps ?? ""}
                onChange={(e) => {
                  let inputValue = e.currentTarget.value;
                  let numericValue = inputValue === "" ? null : parseInt(inputValue.replace(/\D/g, "")); // Handle empty input

                  let max = cacheService.GetGroup()!.maxShareViewIPs
                    ? cacheService.GetGroup()!.maxShareViewIPs
                    : cacheService.GetPlatformSettings()?.maxShareViewIPs ?? 1000;

                  if (inputValue === "" || !max || (numericValue !== null && numericValue <= max)) {
                    onChangeSecurity("maxIps", numericValue);
                  }
                }}
                placeholder={
                  cacheService.GetGroup()!.maxShareViewIPs?.toString() ?? t("field:placeholder.maxIpsNumber")
                }
              />
            </FormControl>
          </Grid>
          <Grid item md={4}>
            <FormControl sx={{ mb: 1 }}>
              <InputLabel shrink htmlFor="maxClips">
                {t("field:label.maxDotNumberOfClips")}
              </InputLabel>
              <OutlinedInput
                id="maxClips"
                type="text"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  max: cacheService.GetGroup()!.maxShareViewClipsNumber
                    ? cacheService.GetGroup()!.maxShareViewClipsNumber
                    : cacheService.GetPlatformSettings()?.maxShareViewClipsNumber ?? 1000,
                }}
                value={securitySection.maxClips ?? ""}
                onChange={(e) => {
                  let inputValue = e.currentTarget.value;
                  let numericValue = inputValue === "" ? null : parseInt(inputValue.replace(/\D/g, "")); // Handle empty input

                  let max = cacheService.GetGroup()!.maxShareViewClipsNumber
                    ? cacheService.GetGroup()!.maxShareViewClipsNumber
                    : cacheService.GetPlatformSettings()?.maxShareViewClipsNumber ?? 1000;

                  if (inputValue === "" || !max || (numericValue !== null && numericValue <= max)) {
                    onChangeSecurity("maxClips", numericValue);
                  }
                }}
                placeholder={
                  cacheService.GetGroup()!.maxShareViewClipsNumber?.toString() ?? t("field:label.maxClipNumber")
                }
              />
            </FormControl>
          </Grid>
          <Grid item md={4}>
            <FormControl sx={{ mb: 1 }}>
              <InputLabel shrink htmlFor="maxClipDuration">
                {t("field:label.maxDurationOfClips")}
              </InputLabel>
              <OutlinedInput
                value={timeSpanToMinutes(securitySection.maxClipDuration)}
                sx={{ width: "100%", mb: 2 }}
                type={"number"}
                inputProps={{
                  max: timeSpanToMinutes(cacheService.GetGroup()!.maxShareViewClipsDuration) ?? undefined,
                }}
                onChange={(e) => {
                  onChangeSecurity && onChangeSecurity("maxClipDuration", minutesToTimeSpan(e.target.value));
                }}
              />
            </FormControl>
          </Grid>
        </Grid>
      </FormSection>
      <FormSection
        icon={<PersonOutlineOutlinedIcon />}
        expanded={expandSection4}
        onExpand={() => setExpandSection4(!expandSection4)}
        title={t("common:recipients")}
        status={recipientsSection.emails.length > 0 ? "complete" : "warning"}
        statusText={
          recipientsSection.emails.length > 0 ? recipientsSection.emails.join(" · ") : t("common:configurationRequired")
        }
      >
        <FormControl sx={{ mb: 2 }}>
          <FormInputEmail
            label={t("field:label.addEmailAddressManually")}
            name="watermark_email"
            onKeyDown={handleKeyDown}
            placeholder={t("field:placeholder.emailAddress")}
            validationProps={{
              formId: "id",
              fieldId: "input-mail",
              required: false,
              initialState: true,
            }}
            stackProps={{
              spacing: 1,
            }}
            value={inputEmail || ""}
            onChange={(e) => setInputEmail(e.target.value)}
          />
        </FormControl>

        <FormControl sx={{ mb: 2 }}>
          <InputLabel htmlFor="emailList" sx={{ mb: 1 }}>
            {t("field:label.loadPreConfiguredMailingList")}
          </InputLabel>
          <Autocomplete
            id="emailList"
            options={recipientsSection.emailList}
            onChange={(e, value) => addEmailFromlist(value?.emails!)}
            popupIcon={<ExpandMoreRoundedIcon />}
            getOptionLabel={(option) => option.name!}
            renderInput={(params) => (
              <OutlinedInput
                {...params.InputProps}
                inputProps={params.inputProps}
                placeholder={t("field:placeholder.preconfiguredMailingListSearch")}
                required
                sx={{ width: "100%" }}
              />
            )}
          />
        </FormControl>

        <FormControl sx={{ mb: 2 }}>
          <InputLabel htmlFor="searchEmail" sx={{ mb: 1 }}>
            {t("field:label.searchEmails")}
          </InputLabel>
          <Autocomplete
            id="searchEmail"
            options={emails}
            onChange={(e, value) => {
              setRecipientsSection(
                produce((draft) => {
                  if (value !== null && value.email && !draft!.emails.includes(value.email!))
                    draft!.emails.push(value.email!);
                })
              );
            }}
            popupIcon={<ExpandMoreRoundedIcon />}
            getOptionLabel={(option) => option.email + " - " + option.name + " (" + option.id + ")"}
            isOptionEqualToValue={(option, value) => option === value}
            value={searchEmailValue}
            renderInput={(params) => (
              <OutlinedInput
                {...params.InputProps}
                inputProps={params.inputProps}
                onChange={(e) => onChangeSearchEmail(e.target.value)}
                placeholder={t("field:placeholder.typeNameOrFirstName")}
                required
                sx={{ width: "100%" }}
              />
            )}
          />
        </FormControl>

        {recentEmails.length > 0 && (
          <Stack mb={2}>
            <InputLabel sx={{ mb: 1 }}>{t("field:label.useRecentEmailList")}</InputLabel>
            <Stack direction="row" alignContent="center" alignItems="center" justifyContent="space-between">
              <Button
                size="small"
                onClick={() =>
                  setRecipientsSection(
                    produce((draft) => {
                      draft!.emails = recentEmails;
                    })
                  )
                }
              >
                {t("button:takeEmail", { count: recentEmails.length })}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {recentEmails.join(", ")}
              </Typography>
            </Stack>
          </Stack>
        )}
        <InputLabel htmlFor="searchEmail" sx={{ mt: 2, mb: 1 }}>
          {t("field:label.recipientsEmailList")}
        </InputLabel>
        <Stack direction="row" spacing={0.2} sx={{ flexWrap: "wrap", gap: 1 }}>
          {recipientsSection.emails.map((email: string) => (
            <Chip
              key={email}
              variant="tag"
              label={email}
              size="medium"
              color="primary"
              onDelete={() => onDeleteEmail(email)}
            />
          ))}
        </Stack>
        {(!recipientsSection.emails || recipientsSection.emails.length === 0) && (
          <Alert severity="info">{t("alert:info.recipientListEmpty")}</Alert>
        )}
      </FormSection>
    </Stack>
  );
});

export default ModalCreateShareStepThree;
